/**
 * Здоровье фидов — служебная страница по адресу /feed-health.
 *
 * В меню её нет намеренно: это диагностика для того, кто держит сервис, а не
 * информация для торгующего. Раньше блок висел на вкладке PnL и занимал место
 * рядом с деньгами, хотя отвечает на совсем другой вопрос — «наши данные
 * свежие или мы врём».
 *
 * Смотреть надо не на «подключено», а на возраст котировок: сокет бывает жив,
 * а данные стоят. Поэтому здесь и медиана, и p90, и доля пар старше 30 секунд.
 *
 * 15.10 — в дизайне терминала: шапка-панель со сводкой плитками, фильтр
 * «Все / С проблемами», липкая шапка таблицы. Цифры обновляются каждые 5 с —
 * поэтому на обновлении ничего не анимируется.
 */
import { whenVisible } from '../utils/poll';
import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Activity, AlertTriangle, Clock, Gauge, Inbox, RefreshCw, Wifi } from 'lucide-react';
import { apiClient } from '../api/client';
import { ExchangeIcon, exchangeLabel } from '../components/ExchangeIcon';
import { SegTabs } from '../ui/SegTabs';
import { LoadingState } from '../ui/State';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

interface Row {
  exchange: string;
  connected: boolean;
  error?: string | null;
  tickers?: number;
  last_tick_age_sec?: number | null;
  age_p50_sec?: number | null;
  age_p90_sec?: number | null;
  stale_30s?: number | null;
  reconnects_1h?: number | null;
  reconnects_24h?: number | null;
}

const num = (v: number | null | undefined, suffix = i18n.t('feedHealth:secSuffix')) =>
  v == null ? '—' : `${v < 1 ? v.toFixed(1) : v.toFixed(0)}${suffix}`;

/** Тон площадки. Зелёный до 3 с, жёлтый до 10, дальше красный: 10 секунд —
 *  это уже спред, посчитанный по вчерашней цене одной из ног. */
type Tone = 'green' | 'yellow' | 'red' | 'muted';
const toneOf = (e: Row): Tone => {
  const p50 = e.age_p50_sec;
  return !e.connected ? 'red' : p50 == null ? 'muted' : p50 < 3 ? 'green' : p50 < 10 ? 'yellow' : 'red';
};
/** Проблемная площадка: нет связи, котировки старше 3 с или сокет часто рвётся. */
const isProblem = (e: Row) => toneOf(e) === 'red' || toneOf(e) === 'yellow' || (e.reconnects_1h ?? 0) > 10;

const FH_CSS = `
.fh-scroll{overflow-x:auto}
@media (min-width:1024px){
  .fh-scroll{overflow:auto;max-height:calc(100dvh - 300px);overscroll-behavior:contain;scrollbar-width:thin;scrollbar-color:hsl(var(--border)) transparent}
  .fh-tbl thead th{position:sticky;top:0;z-index:2}
}
.fh-tbl{width:100%;font-size:13px;font-variant-numeric:tabular-nums;border-collapse:separate;border-spacing:0}
@media (min-width:640px){.fh-tbl{font-size:14px}}
.fh-tbl thead th{height:40px;background:hsl(var(--card));box-shadow:inset 0 -1px 0 hsl(var(--border));
  font-size:11px;font-weight:500;letter-spacing:.05em;text-transform:uppercase;color:hsl(var(--muted-foreground));white-space:nowrap}
.fh-tbl tbody td{padding-top:10px;padding-bottom:10px;border-top:1px solid hsl(var(--border) / .45)}
.fh-tbl tbody tr:first-child td{border-top:0}
.fh-row{transition:background-color .15s}
.fh-row:hover{background:hsl(var(--foreground) / .045)}
.fh-row>td:first-child{box-shadow:inset 3px 0 0 transparent;transition:box-shadow .2s}
.fh-row:hover>td:first-child{box-shadow:inset 3px 0 0 hsl(var(--primary) / .7)}
.fh-dot{width:8px;height:8px;border-radius:999px;flex-shrink:0}
.fh-dot[data-tone="green"]{background:hsl(var(--trade-green));box-shadow:0 0 8px hsl(var(--trade-green) / .7)}
.fh-dot[data-tone="yellow"]{background:rgb(234 179 8);box-shadow:0 0 8px rgb(234 179 8 / .7)}
.fh-dot[data-tone="red"]{background:hsl(var(--trade-red));box-shadow:0 0 8px hsl(var(--trade-red) / .7)}
.fh-dot[data-tone="muted"]{background:hsl(var(--muted-foreground) / .5)}
.fh-age{padding:1px 7px;border-radius:6px}
.fh-age[data-tone="yellow"]{color:rgb(202 138 4);background:rgb(234 179 8 / .12)}
.fh-age[data-tone="red"]{color:hsl(var(--trade-red));background:hsl(var(--trade-red) / .1)}
.dark .fh-age[data-tone="yellow"]{color:rgb(250 204 21)}
:root:not(.dark) .fh-row:hover{background:hsl(var(--primary) / .06)}
@media (prefers-reduced-motion:reduce){.fh-row,.fh-row>td:first-child{transition:none !important}}
`;

export const FeedHealthPage: React.FC = () => {
  const { t } = useTranslation('feedHealth');
  const [rows, setRows] = useState<Row[]>([]);
  const [ts, setTs] = useState<number>(0);
  const [err, setErr] = useState(false);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState<'all' | 'problems'>('all');

  const load = async () => {
    setBusy(true);
    try {
      const r = await apiClient.raw.get('/api/feed-health');
      setRows(r.data?.exchanges || []);
      setTs(Date.now());
      setErr(false);
    } catch { setErr(true); /* не логинен / бэк лежит */ }
    finally { setBusy(false); }
  };

  useEffect(() => {
    void load();
    const id = setInterval(whenVisible(load), 5000);
    return () => clearInterval(id);
  }, []);

  // Сортируем по возрасту: самое протухшее сверху — именно оно и интересно.
  // Площадка без связи — ещё выше: возраста у неё нет, а проблема главная.
  const sorted = useMemo(
    () => [...rows].sort((a, b) => (Number(a.connected) - Number(b.connected))
      || (b.age_p50_sec ?? 0) - (a.age_p50_sec ?? 0)), [rows]);
  const problems = useMemo(() => sorted.filter(isProblem), [sorted]);
  const shown = view === 'problems' ? problems : sorted;

  const kpi = useMemo(() => {
    const live = rows.filter(r => r.connected).length;
    const worst = sorted.find(r => r.connected && r.age_p50_sec != null) ?? null;
    const stale = rows.reduce((s, r) => s + (r.stale_30s ?? 0), 0);
    const rec = rows.reduce((s, r) => s + (r.reconnects_1h ?? 0), 0);
    return { live, worst, stale, rec };
  }, [rows, sorted]);

  const loaded = ts > 0;
  const stateText = (e: Row) => e.error ? e.error.slice(0, 60) : e.connected ? t('state.connected') : t('state.down');

  return (
    <div className="mx-auto max-w-5xl space-y-3 px-3 py-4 text-foreground sm:px-6 sm:py-5">
      <style>{FH_CSS}</style>

      {/* Шапка: название, метка обновления, сводка плитками. */}
      <div className="sb-panel !overflow-visible px-3 py-3 sm:px-4">
        <div className="flex items-center gap-3">
          <span className="sb-badgeicon"><Activity size={16}/></span>
          <div className="min-w-0 flex-1">
            <h1 className="flex flex-wrap items-center gap-x-3 gap-y-1 text-lg font-semibold tracking-tight">
              {t('title')}
              {loaded && (
                <span className="inline-flex items-center gap-1.5 text-[11px] font-normal text-muted-foreground">
                  {err ? <span className="inline-block size-[7px] rounded-full bg-trade-red"/> : <span className="sb-live"/>}
                  {t('updated', { time: new Date(ts).toLocaleTimeString(i18n.language === 'uk' ? 'uk-UA' : 'ru-RU') })}
                </span>
              )}
            </h1>
            <p className="hidden text-[12px] text-muted-foreground sm:block">{t('subtitle')}</p>
          </div>
          <button type="button" onClick={() => void load()} title={t('common:refresh')} aria-label={t('common:refresh')}
            className="sb-iconbtn sb-spinhover shrink-0 gap-1.5 border border-border px-2.5 max-sm:!h-10 max-sm:!min-w-[40px]">
            <RefreshCw size={14} className={busy ? 'animate-spin' : undefined}/>
            <span className="hidden text-[12px] sm:inline">{t('common:refresh')}</span>
          </button>
        </div>
        {loaded && rows.length > 0 && (
          <div className="mt-3 grid grid-cols-2 gap-2 md:grid-cols-4">
            <Kpi icon={<Wifi size={12}/>} label={t('kpi.live')}
                 tone={kpi.live === rows.length ? 'green' : 'red'}
                 value={`${kpi.live} / ${rows.length}`}
                 hint={kpi.live === rows.length ? t('kpi.liveAll') : t('kpi.liveDown', { n: rows.length - kpi.live })}/>
            <Kpi icon={<Clock size={12}/>} label={t('kpi.worst')}
                 tone={kpi.worst ? (toneOf(kpi.worst) === 'green' ? 'green' : toneOf(kpi.worst) === 'muted' ? 'muted' : 'red') : 'muted'}
                 value={kpi.worst ? num(kpi.worst.age_p50_sec) : '—'}
                 hint={kpi.worst ? exchangeLabel(kpi.worst.exchange) : undefined}/>
            <Kpi icon={<Gauge size={12}/>} label={t('kpi.stale')} tone={kpi.stale > 0 ? 'accent' : 'muted'}
                 value={String(kpi.stale)} hint={t('kpi.staleHint')}/>
            <Kpi icon={<AlertTriangle size={12}/>} label={t('kpi.reconnects')} tone={kpi.rec > 10 ? 'red' : 'muted'}
                 value={String(kpi.rec)} hint={t('kpi.reconnectsHint')}/>
          </div>
        )}
      </div>

      {!loaded && !err && <div className="sb-panel sb-panel-2"><LoadingState/></div>}
      {!loaded && err && (
        <div className="sb-plate flex flex-col items-center gap-2 p-8 text-center text-sm text-muted-foreground">
          <AlertTriangle size={22} className="text-trade-red/80"/>
          {t('loadError')}
        </div>
      )}

      {loaded && (
        <div className="sb-panel sb-panel-2">
          <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2.5 sm:px-4">
            <SegTabs<'all' | 'problems'> accent value={view} onChange={setView}
              items={[
                { key: 'all', label: t('view.all'), badge: <span className="sb-count">{sorted.length}</span> },
                { key: 'problems', label: t('view.problems'), badge: <span className="sb-count">{problems.length}</span> },
              ]}/>
            <span className="ml-auto hidden items-center gap-3 text-[11px] text-muted-foreground sm:inline-flex">
              <span className="inline-flex items-center gap-1.5"><span className="fh-dot" data-tone="green"/>{t('legend.fresh')}</span>
              <span className="inline-flex items-center gap-1.5"><span className="fh-dot" data-tone="yellow"/>{t('legend.slow')}</span>
              <span className="inline-flex items-center gap-1.5"><span className="fh-dot" data-tone="red"/>{t('legend.stale')}</span>
            </span>
          </div>

          {shown.length === 0 ? (
            <div className="flex flex-col items-center gap-2 p-10 text-center text-sm text-muted-foreground">
              <Inbox size={24} className="text-muted-foreground/60"/>
              {view === 'problems' ? t('empty.problems') : t('empty.all')}
            </div>
          ) : (
            /* На телефоне три последние колонки прячем (состояние видно по
               точке, ошибка — строкой под именем), а таблица листается внутри
               панели: раньше overflow-hidden молча обрезал всё правее p90. */
            <div className="fh-scroll">
              <table className="fh-tbl">
                <thead>
                  <tr>
                    <th className="px-3 text-left sm:px-4">{t('cols.venue')}</th>
                    <th className="hidden px-3 text-right sm:table-cell">{t('cols.pairs')}</th>
                    <th className="px-3 text-right">{t('cols.median')}</th>
                    <th className="px-3 text-right">p90</th>
                    <th className="hidden px-3 text-right sm:table-cell">{t('cols.stale')}</th>
                    <th className="hidden px-3 text-right sm:table-cell" title={t('cols.reconnectsTitle')}>{t('cols.reconnects')}</th>
                    <th className="hidden px-3 text-left sm:table-cell sm:pr-4">{t('cols.state')}</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map(e => {
                    const tone = toneOf(e);
                    const p90tone: Tone = e.age_p90_sec == null ? 'muted' : e.age_p90_sec < 3 ? 'green' : e.age_p90_sec < 10 ? 'yellow' : 'red';
                    return (
                      <tr key={e.exchange} className="fh-row">
                        <td className="px-3 sm:px-4">
                          <div className="flex items-center gap-2">
                            <span className="fh-dot" data-tone={tone} title={stateText(e)}/>
                            <ExchangeIcon ex={e.exchange} size={16} decorative/>
                            <span className="min-w-0">
                              <span className="block whitespace-nowrap">{exchangeLabel(e.exchange)}</span>
                              {(e.error || !e.connected) && (
                                <span className="block max-w-[160px] truncate text-[11px] text-trade-red sm:hidden">{stateText(e)}</span>
                              )}
                            </span>
                          </div>
                        </td>
                        <td className="hidden px-3 text-right font-mono sm:table-cell">{e.tickers ?? '—'}</td>
                        <td className="px-3 text-right font-mono">
                          <span className="fh-age" data-tone={tone}>{num(e.age_p50_sec)}</span>
                        </td>
                        <td className="px-3 text-right font-mono">
                          <span className="fh-age" data-tone={p90tone}>{num(e.age_p90_sec)}</span>
                        </td>
                        <td className={clsx('hidden px-3 text-right font-mono sm:table-cell',
                            (e.stale_30s ?? 0) > 0 ? 'text-foreground' : 'text-muted-foreground')}>
                          {e.stale_30s == null ? '—' : `${e.stale_30s}`}
                        </td>
                        <td className={clsx('hidden whitespace-nowrap px-3 text-right font-mono sm:table-cell',
                            (e.reconnects_1h ?? 0) > 10 ? 'text-trade-red' : 'text-muted-foreground')}>
                          {e.reconnects_1h ?? 0} / {e.reconnects_24h ?? 0}
                        </td>
                        <td className={clsx('hidden max-w-[220px] truncate px-3 text-[13px] sm:table-cell sm:pr-4',
                            e.error || !e.connected ? 'text-trade-red' : 'text-muted-foreground')}
                            title={e.error || undefined}>
                          {stateText(e)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/** Плитка сводки (.sb-kpi): подпись капсом, крупное число, полоса тона слева. */
const Kpi: React.FC<{
  icon: React.ReactNode; label: string; value: string; hint?: string;
  tone?: 'green' | 'red' | 'muted' | 'accent';
}> = ({ icon, label, value, hint, tone }) => (
  <div data-tone={tone} className="sb-kpi">
    <div className="sb-kpi-label">{icon}<span className="truncate">{label}</span></div>
    <div className={clsx('sb-kpi-value', tone === 'green' ? 'text-trade-green' : tone === 'red' ? 'text-trade-red' : 'text-foreground')}>{value}</div>
    {hint && <div className="truncate text-[11px] text-muted-foreground">{hint}</div>}
  </div>
);

export default FeedHealthPage;
